import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Users, Building2, FileText, Layout as LayoutIcon, RefreshCw, Trash2, Edit3, Plus, X, Upload } from "lucide-react";

const STATUS_OPTIONS = [
  { id: "nuovo", label: "Nuovo" },
  { id: "contattato", label: "Contattato" },
  { id: "in_trattativa", label: "In trattativa" },
  { id: "chiuso", label: "Chiuso" },
];

const STATUS_COLORS = {
  nuovo: "bg-risco-accent-soft text-risco-accent-dark border-risco-accent/50",
  contattato: "bg-blue-600/20 text-blue-400 border-blue-600/40",
  in_trattativa: "bg-purple-600/20 text-purple-400 border-purple-600/40",
  chiuso: "bg-stone-200 text-risco-muted border-stone-400/40",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailQueue, setEmailQueue] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedProp, setSelectedProp] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingProj, setEditingProj] = useState(null);
  const [email, setEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, p, c, q, pr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/proposals"),
        api.get("/admin/contacts"),
        api.get("/admin/email-queue"),
        api.get("/portfolio"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setProposals(p.data);
      setContacts(c.data);
      setEmailQueue(q.data);
      setProjects(pr.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        nav("/admin");
      } else {
        toast.error("Errore caricamento dati");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    // Verifica sessione via cookie (nessun token in localStorage).
    api.get("/auth/me")
      .then((r) => {
        setEmail(r.data.email);
        load();
      })
      .catch(() => nav("/admin"));
  }, [load, nav]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) { /* ignore */ }
    nav("/admin");
  };

  const updateLead = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Aggiornato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const updateProp = async (id, status) => {
    try {
      await api.patch(`/admin/proposals/${id}`, { status });
      toast.success("Aggiornato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const updateContact = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      toast.success("Aggiornato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const retryEmail = async (id) => {
    try {
      await api.post(`/admin/email-queue/${id}/retry`);
      toast.success("Email inviata");
      load();
    } catch (e) {
      toast.error("Retry fallito: " + (e?.response?.data?.detail || e.message));
    }
  };

  const fmtEUR = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
  const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-risco-bg text-risco-ink" data-testid="admin-dashboard">
      <header className="border-b border-risco-border bg-white/70 backdrop-blur">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo-risco.png" alt="Ri.Sco." style={{ height: '40px', width: 'auto' }} />
            <div className="text-xs text-stone-500 border-l border-risco-border pl-3 hidden sm:block">
              <div className="font-medium">Admin</div>
              <div className="text-[10px]">{email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="p-2 rounded border border-stone-300 hover:border-risco-accent text-stone-700"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-3 py-2 rounded border border-stone-300 hover:border-risco-accent text-stone-700 text-sm">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="border-b border-risco-border">
        <div className="container-x flex gap-1 overflow-x-auto">
          {[
            { id: "overview", label: "Overview", icon: LayoutIcon },
            { id: "leads", label: `Preventivi (${leads.length})`, icon: Users },
            { id: "proposals", label: `Proposte (${proposals.length})`, icon: FileText },
            { id: "contacts", label: `Messaggi (${contacts.length})`, icon: FileText },
            { id: "emails", label: `Coda email (${emailQueue.filter(x=>!x.sent).length})`, icon: RefreshCw },
            { id: "projects", label: `Portfolio (${projects.length})`, icon: Building2 },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 whitespace-nowrap ${
                tab === t.id ? "border-risco-accent text-risco-accent-dark" : "border-transparent text-risco-muted hover:text-risco-ink"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="container-x py-8">
        {loading && <div className="text-stone-500">Caricamento...</div>}

        {tab === "overview" && stats && (
          <>
            {(stats.email_queue_permanently_failed || 0) > 0 && (
              <div
                className="mb-5 flex items-start gap-3 p-4 rounded-md border border-red-300 bg-red-50 text-red-900"
                data-testid="admin-permanently-failed-banner"
              >
                <div className="mt-0.5 shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold">
                  {stats.email_queue_permanently_failed}
                </div>
                <div className="text-sm">
                  <strong>Email fallite in modo permanente.</strong> Uno o più messaggi non sono
                  stati consegnati (errore SMTP 5xx: indirizzo inesistente o bloccato antispam).
                  Ogni lead con notifica non recapitata rischia di essere perso — verificalo nella
                  sezione <button
                    className="underline underline-offset-2 font-semibold"
                    onClick={() => setTab("emails")}
                    data-testid="admin-permanently-failed-link"
                  >Coda email</button>.
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
              {[
                { k: "Lead totali", v: stats.leads_total, sub: `${stats.leads_new} nuovi` },
                { k: "Proposte totali", v: stats.proposals_total, sub: `${stats.proposals_new} nuove` },
                { k: "Messaggi contatti", v: stats.contacts_total || 0, sub: `${stats.contacts_new || 0} nuovi` },
                {
                  k: "Coda email",
                  v: (stats.email_queue_pending || 0) + (stats.email_queue_failed || 0) + (stats.email_queue_permanently_failed || 0),
                  sub: `${stats.email_queue_failed || 0} in retry · ${stats.email_queue_permanently_failed || 0} definitive`,
                  danger: (stats.email_queue_permanently_failed || 0) > 0,
                },
              ].map((c) => (
                <div key={c.k} className={`risco-card p-6 ${c.danger ? "border-red-400 bg-red-50/40" : ""}`}>
                  <div className="text-xs text-stone-500 uppercase tracking-widest">{c.k}</div>
                  <div className={`font-heading text-4xl mt-3 ${c.danger ? "text-red-700" : "text-risco-accent"}`}>{c.v}</div>
                  <div className={`text-xs mt-1 ${c.danger ? "text-red-700 font-medium" : "text-risco-muted"}`}>{c.sub}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "leads" && (
          <div className="risco-card overflow-hidden" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead className="bg-risco-dark/60 text-risco-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3">Intervento</th>
                  <th className="text-right px-4 py-3">Stima</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && !loading && (
                  <tr><td colSpan="7" className="text-center py-10 text-stone-500">Nessun lead.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-risco-border hover:bg-risco-alt/40">
                    <td className="px-4 py-3 text-risco-muted whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-risco-ink">{l.name}</td>
                    <td className="px-4 py-3 text-stone-700">
                      <div>{l.email}</div>
                      {l.phone && <div className="text-xs text-stone-500">{l.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-stone-700 text-xs">
                      {l.input?.intervention_type?.replace(/_/g, " ")} · {l.input?.sqm} mq · {l.input?.finish_level}
                    </td>
                    <td className="px-4 py-3 text-right text-risco-accent whitespace-nowrap">
                      {fmtEUR(l.estimate?.min_price || 0)}–{fmtEUR(l.estimate?.max_price || 0)}
                    </td>
                    <td className="px-4 py-3">
                      <select value={l.status} onChange={(e) => updateLead(l.id, e.target.value)} data-testid={`admin-lead-status-${l.id}`}
                        className={`px-2 py-1 rounded border text-xs ${STATUS_COLORS[l.status]}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id} className="bg-white">{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedLead(l)} data-testid={`admin-lead-view-${l.id}`} className="text-risco-accent hover:text-risco-accent-dark text-xs">Dettagli</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "proposals" && (
          <div className="risco-card overflow-hidden" data-testid="admin-proposals-table">
            <table className="w-full text-sm">
              <thead className="bg-risco-dark/60 text-risco-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Accordo</th>
                  <th className="text-left px-4 py-3">Luogo</th>
                  <th className="text-left px-4 py-3">Foto</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {proposals.length === 0 && !loading && (
                  <tr><td colSpan="9" className="text-center py-10 text-stone-500">Nessuna proposta.</td></tr>
                )}
                {proposals.map((p) => (
                  <tr key={p.id} className="border-t border-risco-border hover:bg-risco-alt/40">
                    <td className="px-4 py-3 text-risco-muted whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-risco-ink">{p.name}</td>
                    <td className="px-4 py-3 text-stone-700">
                      <div className="text-xs">{p.email}</div>
                      {p.phone && <div className="text-xs text-stone-500">{p.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-stone-700 text-xs">{p.proposal_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-stone-700 text-xs">{p.agreement_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-stone-700 text-xs">{p.location}{p.sqm ? ` · ${p.sqm}mq` : ""}</td>
                    <td className="px-4 py-3">{p.image_paths?.length || 0}</td>
                    <td className="px-4 py-3">
                      <select value={p.status} onChange={(e) => updateProp(p.id, e.target.value)} data-testid={`admin-prop-status-${p.id}`}
                        className={`px-2 py-1 rounded border text-xs ${STATUS_COLORS[p.status]}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id} className="bg-white">{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedProp(p)} data-testid={`admin-prop-view-${p.id}`} className="text-risco-accent hover:text-risco-accent-dark text-xs">Dettagli</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div className="risco-card overflow-hidden" data-testid="admin-contacts-table">
            <table className="w-full text-sm">
              <thead className="bg-risco-alt text-risco-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3">Messaggio</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && !loading && (
                  <tr><td colSpan="6" className="text-center py-10 text-stone-500">Nessun messaggio.</td></tr>
                )}
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-risco-border hover:bg-risco-alt/40">
                    <td className="px-4 py-3 text-risco-muted whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-risco-ink">{c.name}</td>
                    <td className="px-4 py-3 text-stone-700">
                      <div className="text-xs">{c.email}</div>
                      {c.phone && <div className="text-xs text-stone-500">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-stone-700 text-xs max-w-md truncate">{c.message}</td>
                    <td className="px-4 py-3">
                      <select value={c.status} onChange={(e) => updateContact(c.id, e.target.value)} data-testid={`admin-contact-status-${c.id}`}
                        className={`px-2 py-1 rounded border text-xs ${STATUS_COLORS[c.status]}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id} className="bg-white">{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedContact(c)} data-testid={`admin-contact-view-${c.id}`} className="text-risco-accent hover:text-risco-accent-dark text-xs">Dettagli</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "emails" && (
          <div className="risco-card overflow-hidden" data-testid="admin-emails-table">
            <table className="w-full text-sm">
              <thead className="bg-risco-alt text-risco-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Data</th>
                  <th className="text-left px-4 py-3">Contesto</th>
                  <th className="text-left px-4 py-3">Destinatario</th>
                  <th className="text-left px-4 py-3">Oggetto</th>
                  <th className="text-left px-4 py-3">Tentativi</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">Errore</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {emailQueue.length === 0 && !loading && (
                  <tr><td colSpan="8" className="text-center py-10 text-stone-500">Coda vuota — tutte le email sono state inviate.</td></tr>
                )}
                {emailQueue.map((q) => (
                  <tr key={q.id} className={`border-t border-risco-border ${q.permanently_failed ? "bg-red-50/50" : ""}`} data-testid={q.permanently_failed ? "admin-email-permanently-failed" : undefined}>
                    <td className="px-4 py-3 text-risco-muted whitespace-nowrap text-xs">{fmtDate(q.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-stone-700">{q.context}</td>
                    <td className="px-4 py-3 text-xs">{(q.to || []).join(", ")}</td>
                    <td className="px-4 py-3 text-xs text-stone-700 max-w-xs truncate">{q.subject}</td>
                    <td className="px-4 py-3 text-xs">{q.attempts}/3</td>
                    <td className="px-4 py-3">
                      {q.sent ? (
                        <span className="px-2 py-1 rounded text-xs border border-green-700/40 bg-green-100 text-green-800">Inviata</span>
                      ) : q.permanently_failed ? (
                        <span className="px-2 py-1 rounded text-xs border border-red-700 bg-red-600 text-white font-semibold" title={`SMTP ${q.smtp_code || 5}xx — non ritentabile`}>
                          Definitivo {q.smtp_code || ""}
                        </span>
                      ) : q.attempts >= 3 ? (
                        <span className="px-2 py-1 rounded text-xs border border-red-700/40 bg-red-100 text-red-800">Fallita</span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs border border-amber-700/40 bg-amber-100 text-amber-800">In coda</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate">{q.last_error || ""}</td>
                    <td className="px-4 py-3">
                      {!q.sent && <button onClick={() => retryEmail(q.id)} data-testid={`admin-email-retry-${q.id}`} className="text-risco-accent hover:text-risco-accent-dark text-xs">Riprova ora</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "projects" && (
          <ProjectsManager projects={projects} onReload={load} setEditingProj={setEditingProj} editingProj={editingProj} />
        )}
      </main>

      {/* Modals */}
      {selectedLead && (
        <Modal onClose={() => setSelectedLead(null)}>
          <div data-testid="lead-detail-modal">
            <div className="overline mb-2">Lead #{selectedLead.id.slice(0, 8)}</div>
            <h3 className="font-heading text-2xl text-risco-ink">{selectedLead.name}</h3>
            <div className="text-risco-muted text-sm mt-1">{selectedLead.email}{selectedLead.phone && " · " + selectedLead.phone}</div>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <DetailBlock label="Intervento" value={selectedLead.input?.intervention_type?.replace(/_/g, " ")} />
              <DetailBlock label="Superficie" value={`${selectedLead.input?.sqm} mq`} />
              <DetailBlock label="Città" value={selectedLead.input?.city} />
              <DetailBlock label="Stato attuale" value={selectedLead.input?.current_state?.replace(/_/g, " ")} />
              <DetailBlock label="Finiture" value={selectedLead.input?.finish_level} />
              <DetailBlock label="Extras" value={(selectedLead.input?.extras || []).join(", ") || "—"} />
            </div>
            <div className="mt-6 p-4 rounded border border-risco-accent/40 bg-risco-accent-soft/60">
              <div className="overline mb-2">Stima AI</div>
              <div className="text-risco-accent text-2xl font-heading">
                {fmtEUR(selectedLead.estimate?.min_price)} — {fmtEUR(selectedLead.estimate?.max_price)}
              </div>
              <div className="mt-3 space-y-1 text-sm">
                {(selectedLead.estimate?.breakdown || []).map((b) => (
                  <div key={b.voce} className="flex justify-between text-stone-700"><span>{b.voce}</span><span>{fmtEUR(b.min)}–{fmtEUR(b.max)}</span></div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {selectedProp && (
        <Modal onClose={() => setSelectedProp(null)}>
          <div data-testid="proposal-detail-modal">
            <div className="overline mb-2">Proposta #{selectedProp.id.slice(0, 8)}</div>
            <h3 className="font-heading text-2xl text-risco-ink">{selectedProp.name}</h3>
            <div className="text-risco-muted text-sm mt-1">{selectedProp.email}{selectedProp.phone && " · " + selectedProp.phone}</div>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <DetailBlock label="Tipo" value={selectedProp.proposal_type?.replace(/_/g, " ")} />
              <DetailBlock label="Accordo" value={selectedProp.agreement_type?.replace(/_/g, " ")} />
              <DetailBlock label="Luogo" value={selectedProp.location} />
              <DetailBlock label="Superficie" value={selectedProp.sqm ? `${selectedProp.sqm} mq` : "—"} />
            </div>
            {selectedProp.state_description && <DetailBlock label="Stato" value={selectedProp.state_description} full />}
            {selectedProp.notes && <DetailBlock label="Note" value={selectedProp.notes} full />}
            {selectedProp.image_paths?.length > 0 && (
              <div className="mt-6">
                <div className="overline mb-3">Foto ({selectedProp.image_paths.length})</div>
                <div className="grid grid-cols-3 gap-3">
                  {selectedProp.image_paths.map((p) => (
                    <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square block">
                      <img src={fileUrl(p)} alt="proposta" className="w-full h-full object-cover rounded border border-risco-border hover:border-risco-accent" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
      {selectedContact && (
        <Modal onClose={() => setSelectedContact(null)}>
          <div data-testid="contact-detail-modal">
            <div className="overline mb-2">Messaggio #{selectedContact.id.slice(0, 8)}</div>
            <h3 className="font-heading text-2xl text-risco-ink">{selectedContact.name}</h3>
            <div className="text-risco-muted text-sm mt-1">{selectedContact.email}{selectedContact.phone && " · " + selectedContact.phone}</div>
            <div className="mt-6">
              <div className="text-xs text-stone-500 uppercase tracking-wider">Messaggio</div>
              <div className="bg-risco-alt p-4 rounded mt-2 text-risco-ink whitespace-pre-wrap">{selectedContact.message || "(nessun testo)"}</div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DetailBlock({ label, value, full = false }) {
  return (
    <div className={full ? "md:col-span-2 mt-4" : ""}>
      <div className="text-xs text-stone-500 uppercase tracking-wider">{label}</div>
      <div className="text-risco-ink mt-1">{value || "—"}</div>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-risco-dark/95 flex items-start md:items-center justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="risco-card max-w-3xl w-full p-6 md:p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-risco-muted hover:text-risco-ink text-2xl" data-testid="modal-close">×</button>
        {children}
      </div>
    </div>
  );
}

function ProjectsManager({ projects, onReload, editingProj, setEditingProj }) {
  const [creating, setCreating] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const del = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Progetto eliminato");
      onReload();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const reseed = async () => {
    const ok = window.confirm(
      "ATTENZIONE — Ripristina i 5 progetti ufficiali Ri.Sco.\n\n" +
      "Cancellerà TUTTI i progetti attuali del portfolio e inserirà i 5 progetti seed " +
      "(Bonus Facciata, Vomero, Open space, 9 piani, B&B Chiaia).\n\n" +
      "Le foto caricate manualmente andranno perse. Procedere?"
    );
    if (!ok) return;
    setReseeding(true);
    try {
      const r = await api.post("/admin/projects/reseed");
      toast.success(`Portfolio ripristinato: ${r.data.inserted} progetti (${r.data.deleted} eliminati)`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset portfolio");
    } finally {
      setReseeding(false);
    }
  };

  return (
    <div data-testid="admin-projects-manager">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <div className="overline">Portfolio</div>
          <h2 className="font-heading text-2xl text-risco-ink">Gestione progetti</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reseed}
            disabled={reseeding}
            data-testid="admin-project-reseed"
            title="Cancella tutto e ripristina i 5 progetti ufficiali"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-risco-ink text-risco-ink hover:bg-risco-ink hover:text-risco-bg disabled:opacity-50 font-semibold text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${reseeding ? "animate-spin" : ""}`} />
            {reseeding ? "Ripristino..." : "Ripristina seed"}
          </button>
          <button onClick={() => setCreating(true)} data-testid="admin-project-new" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-risco-accent hover:bg-risco-accent-dark text-white font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nuovo progetto
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="risco-card overflow-hidden" data-testid={`admin-project-${p.id}`}>
            <div className="aspect-video overflow-hidden bg-risco-alt">
              {(p.after_image || p.before_image) && <img src={fileUrl(p.after_image || p.before_image)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="text-xs text-risco-accent uppercase tracking-wider">{p.category?.replace("_", " ")}</div>
              <div className="text-risco-ink font-heading text-sm mt-1">{p.title}</div>
              <div className="text-xs text-stone-500 mt-1">{p.location}</div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditingProj(p)} className="text-xs px-3 py-1.5 rounded border border-stone-300 hover:border-risco-accent text-stone-700 inline-flex items-center gap-1" data-testid={`admin-project-edit-${p.id}`}>
                  <Edit3 className="w-3 h-3" /> Modifica
                </button>
                <button onClick={() => del(p.id)} className="text-xs px-3 py-1.5 rounded border border-red-800 hover:border-red-500 text-red-400 inline-flex items-center gap-1" data-testid={`admin-project-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" /> Elimina
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(creating || editingProj) && (
        <ProjectFormModal
          project={editingProj}
          onClose={() => { setCreating(false); setEditingProj(null); }}
          onSaved={() => { setCreating(false); setEditingProj(null); onReload(); }}
        />
      )}
    </div>
  );
}

function ProjectFormModal({ project, onClose, onSaved }) {
  const [f, setF] = useState({
    title: project?.title || "",
    category: project?.category || "ristrutturazioni",
    location: project?.location || "",
    description: project?.description || "",
    before_image: project?.before_image || "",
    after_image: project?.after_image || "",
    year: project?.year || new Date().getFullYear(),
    featured: project?.featured || false,
    gallery: project?.gallery || [],
  });
  const [saving, setSaving] = useState(false);

  const uploadImg = async (which, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/uploads/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, [which]: res.data.path }));
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error("Errore upload");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f, year: parseInt(f.year) || null };
      if (project) {
        await api.put(`/admin/projects/${project.id}`, payload);
      } else {
        await api.post("/admin/projects", payload);
      }
      toast.success(project ? "Progetto aggiornato" : "Progetto creato");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-risco-dark/95 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose} data-testid="project-form-modal">
      <form onSubmit={save} className="risco-card max-w-3xl w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-risco-muted hover:text-risco-ink"><X className="w-5 h-5" /></button>
        <div className="overline mb-2">{project ? "Modifica" : "Nuovo"}</div>
        <h3 className="font-heading text-2xl text-risco-ink">Progetto portfolio</h3>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="md:col-span-2">
            <label className="text-sm text-stone-700">Titolo *</label>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="proj-form-title" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
          </div>
          <div>
            <label className="text-sm text-stone-700">Categoria</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="proj-form-category" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none">
              <option value="ristrutturazioni">Ristrutturazioni</option>
              <option value="edificazioni">Edificazioni</option>
              <option value="operazioni_immobiliari">Operazioni Immobiliari</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-stone-700">Anno</label>
            <input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="proj-form-year" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-stone-700">Luogo</label>
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="proj-form-location" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-stone-700">Descrizione</label>
            <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="proj-form-description" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
          </div>
          <div>
            <label className="text-sm text-stone-700">Immagine "Prima"</label>
            <div className="mt-1 flex items-center gap-2">
              {f.before_image && <img src={fileUrl(f.before_image)} alt="" className="w-16 h-16 object-cover rounded border border-stone-300" />}
              <label className="flex-1 py-3 px-3 rounded-md border border-stone-300 hover:border-risco-accent text-stone-700 text-sm text-center cursor-pointer">
                <Upload className="w-4 h-4 inline mr-2" /> Carica
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg("before_image", e.target.files[0])} />
              </label>
            </div>
          </div>
          <div>
            <label className="text-sm text-stone-700">Immagine "Dopo"</label>
            <div className="mt-1 flex items-center gap-2">
              {f.after_image && <img src={fileUrl(f.after_image)} alt="" className="w-16 h-16 object-cover rounded border border-stone-300" />}
              <label className="flex-1 py-3 px-3 rounded-md border border-stone-300 hover:border-risco-accent text-stone-700 text-sm text-center cursor-pointer">
                <Upload className="w-4 h-4 inline mr-2" /> Carica
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImg("after_image", e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-8">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-stone-300 text-stone-700">Annulla</button>
          <button type="submit" disabled={saving} data-testid="proj-form-save" className="flex-1 py-3 rounded-md bg-risco-accent hover:bg-risco-accent-dark text-white font-semibold disabled:opacity-50">
            {saving ? "Salvataggio..." : (project ? "Aggiorna" : "Crea")}
          </button>
        </div>
      </form>
    </div>
  );
}
